import { createStore } from 'vuex'
import axios from 'axios'

// API URL'sini ayarla - backend URL'i
const API_URL = 'https://api.modernkurs.com'

// Axios base URL'ini ayarla
axios.defaults.baseURL = API_URL

// D-20: Cache-Control artık default olarak eklenmiyor.
// Sadece auth/login isteğinde manuel ekleniyor (Login response'unun cache'lenmesini önlemek için).
// GET isteklerinde no-cache aç, CDN/browser cache avantajını gereksiz yere kaybediyoruz.

// Axios interceptor - tüm isteklere token ekle ve token yoksa korumalı istekleri engelle
axios.interceptors.request.use(function (config) {
  const token = localStorage.getItem('token')

  // Logout bayrağı ile tüm istekleri kes (örn: sayfa henüz unmount olmadan)
  if (window.isLoggedOut) {
    console.log('🚫 İstek engellendi: Logout bayrağı aktif')
    return Promise.reject(new axios.Cancel('Logged out - request blocked'))
  }

  // Korumalı endpoint tespiti (basit): /api altı ve login/healthz dışında
  const url = (config.url || '').toString()
  const isApi = url.includes('/api/')
  const isPublic = url.includes('/auth/login') || url.includes('/healthz') || url.includes('/health')

  // D-20: Sadece auth-hassas endpoint'lere no-cache ekle
  const isAuthSensitive = url.includes('/login') || url.includes('/logout')
  if (isAuthSensitive) {
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate'
    config.headers['Pragma'] = 'no-cache'
  }

  if (!token) {
    // Token yoksa korumalı isteği hiç göndermeyelim
    if (isApi && !isPublic) {
      console.log('🚫 İstek engellendi: Token yok', url)
      return Promise.reject(new axios.Cancel('No token - request blocked'))
    }
  } else {
    // Token'ın geçerli formatını kontrol et ve header'a ekle
    const tokenParts = token.split('.')
    if (tokenParts.length === 3) {
      config.headers.Authorization = `Bearer ${token}`
    } else {
      console.warn('Geçersiz token formatı, token temizleniyor')
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      return Promise.reject(new axios.Cancel('Invalid token - request blocked'))
    }
  }

  return config
}, function (error) {
  return Promise.reject(error)
})

// Response interceptor - 401 hatalarında token'ı temizle
axios.interceptors.response.use(
  function (response) {
    return response
  },
  function (error) {
    if (error.response && error.response.status === 401) {
      console.log('401 Unauthorized - Token temizleniyor')
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      
      // Store'daki state'leri temizle
      if (window.store) {
        window.store.commit('clearCurrentUser')
        window.store.commit('setAppointments', { userId: 'logout', appointments: {} })
        window.store.commit('setLessons', [])
        window.store.commit('setTeachers', [])
        window.store.commit('setUsers', [])
      }
      
      // Sayfayı login'e yönlendir (Vue Router kullan)
      if (window.location.pathname !== '/login') {
        // Vue Router kullanarak yönlendir
        if (window.router) {
          window.router.push('/login')
        } else {
          // Fallback: sayfa yenileme
          window.location.href = '/login'
        }
      }
    }
    return Promise.reject(error)
  }
)

export default createStore({
  state: {
    lessons: [],
    teachers: [],
    appointments: {}, // Kullanıcı bazında randevular: { userId: [...], userId2: [...] }
    availableTimes: [],
    users: [],
    currentUser: null,
    teacherLessons: [],
    slotSettings: null // system_config'ten yüklenir; hafta içi/sonu slot süresi, mola, öğle
  },
  getters: {
    getLessons: state => state.lessons || [],
    getTeachers: state => state.teachers || [],
    getAppointments: state => {
      // Eğer currentUser yoksa boş array döndür
      if (!state.currentUser?.id) return []
      // Kullanıcı bazında randevuları döndür
      return state.appointments[state.currentUser.id] || []
    },
    getAvailableTimes: state => state.availableTimes || [],
    getUsers: state => state.users || [],
    getCurrentUser: state => state.currentUser,
    getTeacherLessons: state => state.teacherLessons || [],
    getSlotSettings: state => state.slotSettings
  },
  mutations: {
    setLessons(state, lessons) { state.lessons = lessons || [] },
    setTeachers(state, teachers) { state.teachers = teachers || [] },
    setAppointments(state, { userId, appointments }) {
      // Kullanıcı bazında randevuları sakla
      state.appointments = { ...state.appointments, [userId]: appointments || [] }
    },
    setAvailableTimes(state, times) { state.availableTimes = times || [] },
    setUsers(state, users) { state.users = users || [] },
    setCurrentUser(state, user) { state.currentUser = user },
    clearCurrentUser(state) { state.currentUser = null },
    setTeacherLessons(state, teacherLessons) { state.teacherLessons = teacherLessons || [] },
    setSlotSettings(state, settings) { state.slotSettings = settings }
  },
  actions: {
    // Token geçerliliğini kontrol et
    async checkTokenValidity({ commit }) {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          return false
        }

        // Token'ı test etmek için lessons endpoint'ini kullan (öğrenci/admin erişebilir)
        const response = await axios.get('/api/lessons')
        return response.status === 200
      } catch (error) {
        console.error('Token geçersiz:', error)
        // Token geçersizse localStorage'ı temizle
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        commit('clearCurrentUser')
        return false
      }
    },

    // Mevcut kullanıcı bilgisini localStorage'dan yükle
    fetchCurrentUser({ commit }) {
      try {
        const userStr = localStorage.getItem('user')
        const token = localStorage.getItem('token')
        
        if (userStr && token) {
          const userData = JSON.parse(userStr)
          commit('setCurrentUser', userData)
          return userData
        }
        return null
      } catch (error) {
        console.error('Kullanıcı bilgisi yüklenemedi:', error)
        return null
      }
    },

    // Kullanıcı işlemleri
    logout({ commit, dispatch }) {
      console.log('🚪 Logout işlemi başlatılıyor...')
      
      // Local storage'ı temizle
      localStorage.removeItem('user')
      localStorage.removeItem('token')

      // Global logout bayrağı - istekleri anında kes
      window.isLoggedOut = true
      
      // Store'daki tüm state'leri temizle
      commit('clearCurrentUser')
      commit('setAppointments', { userId: 'logout', appointments: {} })
      commit('setLessons', [])
      commit('setTeachers', [])
      commit('setUsers', [])
      
      // Tüm pending request'leri iptal et
      if (window.axiosCancelToken) {
        window.axiosCancelToken.cancel('Logout işlemi')
      }
      
      console.log('✅ Logout işlemi tamamlandı')
      return { success: true }
    },

    // Token geçerliliğini kontrol et
    async checkTokenValidity({ commit, state }) {
      const token = localStorage.getItem('token')
      const user = localStorage.getItem('user')
      
      // Token veya user yoksa temizle
      if (!token || !user) {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        commit('clearCurrentUser')
        return false
      }
      
      try {
        const userData = JSON.parse(user)
        
        // JWT token'ı decode et ve süresini kontrol et
        const tokenParts = token.split('.')
        if (tokenParts.length !== 3) {
          throw new Error('Geçersiz token formatı')
        }
        
        // Base64 decode işlemini güvenli hale getir
        let payload
        try {
          // Base64 padding ekle
          let base64 = tokenParts[1]
          while (base64.length % 4) {
            base64 += '='
          }
          payload = JSON.parse(atob(base64))
        } catch (decodeError) {
          console.error('Token decode hatası:', decodeError)
          throw new Error('Token decode edilemedi')
        }
        
        const currentTime = Math.floor(Date.now() / 1000)
        
        // Token süresi dolmuş mu kontrol et (sadece dolmuş olanları temizle)
        if (payload.exp && payload.exp < currentTime) {
          console.log('Token süresi dolmuş, temizleniyor...')
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          commit('clearCurrentUser')
          return false
        }
        
        // Token geçerli, kullanıcı bilgisini set et
        commit('setCurrentUser', userData)
        return true
      } catch (error) {
        console.error('Token kontrolü hatası:', error)
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        commit('clearCurrentUser')
        return false
      }
    },
    // Slot ayarları — public endpoint (hafta içi/sonu slot süresi, mola, öğle)
    async fetchSlotSettings({ commit }) {
      try {
        const response = await axios.get('/api/config/slot-settings')
        commit('setSlotSettings', response.data)
        return { success: true, data: response.data }
      } catch (error) {
        return { success: false, error: error.response?.data?.error || 'Slot ayarları alınamadı' }
      }
    },

    // Slot ayarlarını güncelle — admin (kaydeder ve TÜM hoca müsaitliklerini siler)
    async updateSlotSettings({ commit }, settings) {
      try {
        const response = await axios.patch('/api/admin/config/slot-settings', settings)
        commit('setSlotSettings', response.data.settings)
        return { success: true, data: response.data }
      } catch (error) {
        return { success: false, error: error.response?.data?.error || 'Slot ayarları kaydedilemedi' }
      }
    },

    // Ders işlemleri
    async fetchLessons({ commit }) {
      try {
        const response = await axios.get('/api/lessons')
        commit('setLessons', response.data)
        return { success: true }
      } catch (error) {
        commit('setLessons', [])
        return { success: false, error: error.response?.data?.error || 'Dersler alınamadı' }
      }
    },

    // Ders oluşturma
    async createLesson({ dispatch }, lessonData) {
      try {
        await axios.post('/api/lessons', lessonData)
        // Dersleri yeniden yükle
        await dispatch('fetchLessons')
        return { success: true }
      } catch (error) {
        return { success: false, error: error.response?.data?.error || 'Ders oluşturulamadı' }
      }
    },

    // Ders silme
    async deleteLesson({ dispatch }, lessonId) {
      try {
        await axios.delete(`/api/lessons/${lessonId}`)
        // Dersleri yeniden yükle
        await dispatch('fetchLessons')
        return { success: true }
      } catch (error) {
        return { success: false, error: error.response?.data?.error || 'Ders silinemedi' }
      }
    },

    // Teacher actions (now using user actions)
    async fetchTeachers({ commit, state }) {
      try {
        // Logout sonrası istekleri engelle
        if (window.isLoggedOut) {
          console.log('🚫 fetchTeachers engellendi: Logout bayrağı aktif')
          return { success: false, error: 'Logout sonrası istek engellendi' }
        }

        // Kullanıcı rolüne göre farklı endpoint kullan
        const currentUser = state.currentUser
        let endpoint = '/api/teachers-with-lessons' // Admin için
        
        if (currentUser && currentUser.role === 'ogrenci') {
          endpoint = '/api/teachers-for-student' // Öğrenci için
        }
        
        const response = await axios.get(endpoint)
        commit('setTeachers', response.data)
        return { success: true }
      } catch (error) {
        // Axios Cancel hatası ise sessizce geç
        if (error.name === 'Cancel' || error.message?.includes('blocked')) {
          console.log('🚫 fetchTeachers iptal edildi:', error.message)
          return { success: false, error: 'İstek iptal edildi' }
        }
        
        console.error('Hocalar alınamadı:', error)
        commit('setTeachers', [])
        return { success: false, error: error.response?.data?.error || 'Hocalar alınamadı' }
      }
    },

    // Hoca derslerini getir
    async fetchTeacherLessons({ commit }, teacherId) {
      try {
        const response = await axios.get(`/api/teacher-lessons/teacher/${teacherId}`)
        commit('setTeacherLessons', response.data)
        return { success: true }
      } catch (error) {
        console.error('Hoca dersleri alınamadı:', error)
        commit('setTeacherLessons', [])
        return { success: false, error: error.response?.data?.error || 'Hoca dersleri alınamadı' }
      }
    },

    // Randevu işlemleri
    async fetchAppointments({ commit, getters }, userId) {
      // Eğer userId verilmezse, mevcut kullanıcı ID'sini kullan
      const targetUserId = userId || getters.getCurrentUser?.id
      
      if (!targetUserId) {
        console.warn('⚠️ fetchAppointments: user_id bulunamadı')
        commit('setAppointments', { userId: 'unknown', appointments: [] })
        return { success: false, error: 'Kullanıcı bilgisi bulunamadı' }
      }
      
      try {
        const response = await axios.get(`/api/appointments?user_id=${targetUserId}`)
        commit('setAppointments', { userId: targetUserId, appointments: response.data })
        return { success: true }
      } catch (error) {
        // Axios Cancel hatası ise sessizce geç
        if (error.name === 'Cancel' || error.message?.includes('blocked')) {
          console.log('🚫 fetchAppointments iptal edildi:', error.message)
          return { success: false, error: 'İstek iptal edildi' }
        }
        console.error('❌ fetchAppointments hatası:', error)
        commit('setAppointments', { userId: targetUserId, appointments: [] })
        return { success: false, error: error.response?.data?.error || 'Randevular alınamadı' }
      }
    },
    
    // Admin için tüm randevuları getir
    async fetchAllAppointments({ commit }) {
      try {
        const response = await axios.get('/api/appointments/all')
        // Admin için tüm randevuları 'all' key'i ile sakla
        commit('setAppointments', { userId: 'all', appointments: response.data })
        return { success: true }
      } catch (error) {
        commit('setAppointments', { userId: 'all', appointments: [] })
        return { success: false, error: error.response?.data?.error || 'Tüm randevular alınamadı' }
      }
    },
    async fetchAvailableTimes({ commit }, { teacherId, date }) {
      try {
        const response = await axios.get(`/api/available-times?teacher_id=${teacherId}&date=${date}`)
        commit('setAvailableTimes', response.data.available_times || [])
        return { success: true }
      } catch (error) {
        commit('setAvailableTimes', [])
        return { success: false, error: error.response?.data?.error || 'Uygun saatler alınamadı' }
      }
    },
    async createAppointment({ dispatch, getters }, appointmentData) {
      try {
        await axios.post('/api/appointments', appointmentData)
        // Randevuları yeniden yükle - mevcut kullanıcı için
        const currentUserId = getters.getCurrentUser?.id
        if (currentUserId) {
          await dispatch('fetchAppointments', currentUserId)
        }
        return { success: true }
      } catch (error) {
        return { success: false, error: error.response?.data?.error || 'Randevu oluşturulamadı' }
      }
    },
    async deleteAppointment({ dispatch, getters }, appointmentId) {
      try {
        await axios.delete(`/api/appointments/${appointmentId}`)
        // Randevuları yeniden yükle - mevcut kullanıcı için
        const currentUserId = getters.getCurrentUser?.id
        if (currentUserId) {
          await dispatch('fetchAppointments', currentUserId)
        }
        return { success: true }
      } catch (error) {
        return { success: false, error: error.response?.data?.error || 'Randevu silinemedi' }
      }
    },
    async updateAppointmentStatus({ dispatch, getters }, { appointmentId, status, cancelledBy }) {
      try {
        const payload = { status }
        if (cancelledBy) {
          payload.cancelled_by = cancelledBy
        }
        
        await axios.patch(`/api/appointments/${appointmentId}/status`, payload)
        // Randevuları yeniden yükle - mevcut kullanıcı için
        const currentUserId = getters.getCurrentUser?.id
        if (currentUserId) {
          await dispatch('fetchAppointments', currentUserId)
        }
        return { success: true }
      } catch (error) {
        return { success: false, error: error.response?.data?.error || 'Randevu durumu güncellenemedi' }
      }
    },
    // Ders güncelleme işlemi
    async updateLesson({ dispatch }, lessonData) {
      try {
        const response = await axios.put(`/api/lessons/${lessonData.id}`, lessonData)
        return { success: true, data: response.data }
      } catch (error) {
        console.error('Ders güncelleme hatası:', error)
        return { success: false, error: error.response?.data?.error || 'Ders güncellenemedi' }
      }
    },

    // Hoca güncelleme işlemi
    async updateTeacher({ dispatch }, teacherData) {
      try {
        const response = await axios.put(`/api/teachers/${teacherData.id}`, teacherData)
        if (response.status === 200) {
          await dispatch('fetchTeachers')
          return { success: true }
        }
      } catch (error) {
        console.error('Hoca güncelleme hatası:', error)
        return { success: false, error: error.response?.data?.error || 'Hoca güncellenemedi' }
      }
    },

    // User actions
    async fetchUsers({ commit }) {
      try {
        
        const response = await axios.get('/api/users')
        
        // Backend direkt array döndürüyor, success field'ı yok
        commit('setUsers', response.data)
        
      } catch (error) {
        console.error('Kullanıcılar alınamadı:', error)
        commit('setUsers', [])
      }
    },

    async createUser({ dispatch }, userData) {
      try {
        const response = await axios.post('/api/users', userData)
        
        
        
        if (response.status === 201) {
          await dispatch('fetchUsers')
          return { success: true, data: response.data }
        }
      } catch (error) {
        console.error('Kullanıcı oluşturma hatası:', error)
        return { success: false, error: error.response?.data?.error || 'Kullanıcı oluşturulamadı' }
      }
    },

    async updateUser({ dispatch }, userData) {
      try {
        const response = await axios.put(`/api/users/${userData.id}`, userData)
        if (response.status === 200) {
          await dispatch('fetchUsers')
          return { success: true }
        }
      } catch (error) {
        console.error('Kullanıcı güncelleme hatası:', error)
        return { success: false, error: error.response?.data?.error || 'Kullanıcı güncellenemedi' }
      }
    },

    async deleteUser({ dispatch }, userId) {
      try {
        const response = await axios.delete(`/api/users/${userId}`)
        
        if (response.status === 204) {
          await dispatch('fetchUsers')
          return { success: true }
        }
      } catch (error) {
        console.error('Kullanıcı silme hatası:', error)
        return { success: false, error: error.response?.data?.error || 'Kullanıcı silinemedi' }
      }
    },

    // Teacher Availability actions
    async fetchTeacherAvailability({ commit }, params) {
      try {
        let url = `/api/teacher-availability/${params.teacherId}`
        if (params.weekStart) {
          url += `?week_start=${params.weekStart}`
        }
        
        
        
        const response = await axios.get(url)
        const data = response.data
        
        
        
        if (data.success) {
          return { success: true, data: data.data }
        } else {
          return { success: false, error: data.error }
        }
      } catch (error) {
        console.error('Müsaitlik bilgileri alınamadı:', error)
        return { success: false, error: 'Müsaitlik bilgileri alınamadı' }
      }
    },

    async createTeacherAvailability({ dispatch }, availabilityData) {
      try {
        const response = await axios.post('/api/teacher-availability', availabilityData)
        
        const data = response.data
        
        if (data.success) {
          return { success: true, message: data.message }
        } else {
          return { success: false, error: data.error }
        }
      } catch (error) {
        console.error('Müsaitlik bilgileri kaydedilemedi:', error)
        return { success: false, error: 'Müsaitlik bilgileri kaydedilemedi' }
      }
    }
  }
}) 